'use client';

import { captureException } from '@inkitt/utils-web/lazy-sentry';
import { useEffect } from 'react';

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <style>{`
          /* Hex values sourced from design tokens in apps/galatea-web/src/app/global.css
             If tokens change, update these values manually to stay in sync:
             #1b1b1c = --color-neutral-900
             #f8f8fb = --color-neutral-200
             #8e8e92 = --color-neutral-500
             #00925e = --color-primary-300 (--color-green-300)
             #33a87e = --color-primary-200 (--color-green-200)
             #212123 = --color-neutral-800
          */
          *, *::before, *::after { box-sizing: border-box; }
          body { margin: 0; min-height: 100vh; background-color: #1b1b1c; color: #ffffff; font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif; -webkit-font-smoothing: antialiased; }
          .container { padding: 10rem 1rem; max-width: 80rem; margin: 0 auto; }
          .inner { max-width: 24rem; margin: 0 auto; text-align: center; display: flex; flex-direction: column; align-items: center; }
          .headline { display: block; margin-bottom: 1rem; margin-top: 1.5rem; font-size: 1.5rem; font-weight: 700; letter-spacing: -0.025em; color: #f8f8fb; }
          .subheadline { display: block; margin-bottom: 2rem; font-size: 1rem; font-weight: 500; color: #8e8e92; line-height: 1.6; }
          .actions { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; }
          .btn-primary { font-weight: 700; width: 245px; padding: 0.5rem 1.5rem; background-color: #00925e; border: none; border-radius: 0.375rem; color: #ffffff; cursor: pointer; font-size: 1rem; transition: background-color 0.15s; }
          .btn-primary:hover { background-color: #33a87e; }
          .btn-secondary { font-weight: 700; width: 245px; padding: 0.5rem 1.5rem; background-color: transparent; border: 1px solid #f8f8fb; border-radius: 0.375rem; color: #ffffff; text-decoration: none; font-size: 1rem; transition: background-color 0.15s; display: block; text-align: center; }
          .btn-secondary:hover { background-color: #212123; }
        `}</style>
      </head>
      {/* Note: Tailwind is not available in this file — className values below refer to plain CSS classes defined in the <style> tag above, not Tailwind utilities */}
      <body>
        <div className="container">
          <div className="inner">
            <img src="/images/not-found/not-found-image.svg" alt="error" height={233} width={200} />
            <p className="headline">Something went wrong on our end.</p>
            <p className="subheadline">An unexpected server error occurred. Please try again shortly.</p>
            <div className="actions">
              <button onClick={reset} className="btn-primary">
                Try Again
              </button>
              {/* locale is unavailable in global-error since the root layout has crashed — defaults to / */}
              <a href="/" className="btn-secondary">
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
